import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import './LoginActivityView.css';

type RoleFilter = 'all' | 'corporate' | 'regional' | 'district' | 'dealer';

interface LoginEvent {
  user_id: number;
  username: string;
  display_name: string;
  role: string;
  login_at: string;
}

interface TopUser {
  _id: number;
  username: string;
  display_name: string;
  role: string;
  login_count: number;
  last_login: string | null;
}

interface ActivitySummary {
  logins_today: number;
  logins_this_week: number;
  logins_this_month: number;
  active_users_this_month: number;
  by_role: Array<{ _id: string; count: number }>;
  top_users: TopUser[];
}

const ROLE_LABELS: Record<string, string> = {
  corporate: 'Corporate',
  regional: 'Regional',
  district: 'District',
  dealer: 'Dealer',
  admin: 'Admin',
};

const ROLE_COLORS: Record<string, string> = {
  corporate: '#6f42c1',
  regional: '#0d6efd',
  district: '#198754',
  dealer: '#fd7e14',
  admin: '#dc3545',
};

function formatRelativeTime(isoString?: string | null): string {
  if (!isoString) return 'Never';
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function formatAbsoluteTime(isoString?: string | null): string {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString();
}

const LoginActivityView: React.FC = () => {
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [events, setEvents] = useState<LoginEvent[]>([]);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchSummary = useCallback(async () => {
    try {
      const data = await adminAPI.getActivitySummary();
      if (data.success) setSummary(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load activity summary');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const params: any = { limit: 50 };
      if (roleFilter !== 'all') params.role = roleFilter;
      const data = await adminAPI.getRecentActivity(params);
      if (data.success) setEvents(data.events);
    } catch (err: any) {
      setError(err.message || 'Failed to load activity feed');
    } finally {
      setLoadingEvents(false);
    }
  }, [roleFilter]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      fetchEvents();
      setLastRefresh(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, [fetchEvents]);

  const handleManualRefresh = () => {
    fetchEvents();
    fetchSummary();
    setLastRefresh(new Date());
  };

  return (
    <div className="login-activity-view">
      <div className="view-header">
        <div>
          <h2>Login Activity</h2>
          <p className="view-subtitle">Monitor who is logging in and how often</p>
        </div>
        <div className="refresh-control">
          <span className="last-refresh-label">Updated {formatRelativeTime(lastRefresh.toISOString())}</span>
          <button className="btn-secondary" onClick={handleManualRefresh}>
            <span className="material-icons">refresh</span>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span className="material-icons">error</span>
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <section className="summary-section">
        <h3 className="section-title">Overview</h3>
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-card-icon" style={{ background: '#e8f0fe', color: '#1a73e8' }}>
              <span className="material-icons">today</span>
            </div>
            <div className="summary-card-body">
              <div className="summary-card-value">
                {loadingSummary ? '—' : summary?.logins_today ?? 0}
              </div>
              <div className="summary-card-label">Logins Today</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-icon" style={{ background: '#e6f4ea', color: '#198754' }}>
              <span className="material-icons">date_range</span>
            </div>
            <div className="summary-card-body">
              <div className="summary-card-value">
                {loadingSummary ? '—' : summary?.logins_this_week ?? 0}
              </div>
              <div className="summary-card-label">This Week</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-icon" style={{ background: '#fce8e6', color: '#d93025' }}>
              <span className="material-icons">calendar_month</span>
            </div>
            <div className="summary-card-body">
              <div className="summary-card-value">
                {loadingSummary ? '—' : summary?.logins_this_month ?? 0}
              </div>
              <div className="summary-card-label">This Month</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-icon" style={{ background: '#fef3e2', color: '#e37400' }}>
              <span className="material-icons">group</span>
            </div>
            <div className="summary-card-body">
              <div className="summary-card-value">
                {loadingSummary ? '—' : summary?.active_users_this_month ?? 0}
              </div>
              <div className="summary-card-label">Active Users (30d)</div>
            </div>
          </div>
        </div>
      </section>

      <div className="two-col-layout">
        {/* Recent Login Feed */}
        <section className="feed-section">
          <div className="section-header">
            <h3 className="section-title">Recent Logins</h3>
            <div className="role-filter-tabs">
              {(['all', 'corporate', 'regional', 'district', 'dealer'] as RoleFilter[]).map((r) => (
                <button
                  key={r}
                  className={`role-tab ${roleFilter === r ? 'active' : ''}`}
                  onClick={() => setRoleFilter(r)}
                >
                  {r === 'all' ? 'All' : ROLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          {loadingEvents ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading activity...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="empty-state">
              <span className="material-icons">login</span>
              <p>No login events found{roleFilter !== 'all' ? ` for ${ROLE_LABELS[roleFilter]}` : ''}.</p>
            </div>
          ) : (
            <div className="feed-table-container">
              <table className="feed-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>When</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="user-cell">
                          <span className="material-icons user-icon">
                            {event.role === 'dealer' ? 'store' : 'person'}
                          </span>
                          <div>
                            <div className="user-display-name">{event.display_name || event.username}</div>
                            <div className="user-username">{event.username}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className="role-pill"
                          style={{
                            background: `${ROLE_COLORS[event.role] ?? '#999'}22`,
                            color: ROLE_COLORS[event.role] ?? '#999',
                            borderColor: `${ROLE_COLORS[event.role] ?? '#999'}55`,
                          }}
                        >
                          {ROLE_LABELS[event.role] ?? event.role}
                        </span>
                      </td>
                      <td className="relative-time">{formatRelativeTime(event.login_at)}</td>
                      <td className="absolute-time">{formatAbsoluteTime(event.login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Top Users Table */}
        <section className="top-users-section">
          <h3 className="section-title">Most Active Users (30 days)</h3>

          {loadingSummary ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
            </div>
          ) : !summary?.top_users?.length ? (
            <div className="empty-state">
              <span className="material-icons">bar_chart</span>
              <p>No login data yet.</p>
            </div>
          ) : (
            <div className="top-users-table-container">
              <table className="top-users-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Logins</th>
                    <th>Last Login</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.top_users.map((user, idx) => (
                    <tr key={user._id}>
                      <td className="rank-cell">{idx + 1}</td>
                      <td>
                        <div className="user-cell">
                          <span className="material-icons user-icon">
                            {user.role === 'dealer' ? 'store' : 'person'}
                          </span>
                          <div>
                            <div className="user-display-name">{user.display_name || user.username}</div>
                            <div className="user-username">{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className="role-pill"
                          style={{
                            background: `${ROLE_COLORS[user.role] ?? '#999'}22`,
                            color: ROLE_COLORS[user.role] ?? '#999',
                            borderColor: `${ROLE_COLORS[user.role] ?? '#999'}55`,
                          }}
                        >
                          {ROLE_LABELS[user.role] ?? user.role}
                        </span>
                      </td>
                      <td className="login-count">{user.login_count}</td>
                      <td className="relative-time">{formatRelativeTime(user.last_login)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default LoginActivityView;
